<!-- table states -->
<div
  class="h-full overflow-auto rounded-xl bg-white"
  [class.border]="hasBorder()"
>
  <div class="flex h-full flex-col gap-3">
    @if (!isLoading && !data?.ungroupedData?.length) {
      <ng-container [ngTemplateOutlet]="emptyTemplate" />
    }

    @if (isLoading && hasLoadingAnimation()) {
      <ng-container [ngTemplateOutlet]="loadingTemplate" />
    }

    @if (!isLoading && data?.ungroupedData?.length) {
      <ng-container [ngTemplateOutlet]="tableTemplate" />
    }
  </div>
</div>

<ng-template #emptyTemplate>
  <div
    class="grid h-full w-full place-items-center gap-2 rounded-xl bg-white p-5"
  >
    <div class="grid grid-cols-[2rem_1fr] place-items-center gap-3">
      <svg-icon
        class="w-fit scale-[2]"
        svgClass="text-steel-blue-500"
        src="/assets/icons/general/warning.svg"
      />
      <div class="flex flex-col gap-1">
        <h2 class="text-lg font-semibold">No data found.</h2>
        Try adjusting the filters or search criteria to view data.
      </div>
    </div>
  </div>
</ng-template>

<ng-template #loadingTemplate>
  <div
    class="grid h-full w-full place-items-center gap-2 rounded-xl bg-white p-5"
  >
    <ag1-loader />
  </div>
</ng-template>

<ng-template #tableTemplate>
  <table
    [id]="tableName()"
    class="mb-2 min-w-max border-separate border-spacing-0 bg-white"
  >
    <ng-container [ngTemplateOutlet]="tableHeaderTemplate" />
    <ng-container [ngTemplateOutlet]="tableBodyTemplate" />
  </table>
</ng-template>

<ng-template #tableHeaderTemplate>
  <ng-template #headerCellStructureTemplate let-field="field" let-index="index">
    <th
      [attr.draggable]="true"
      (dragstart)="onColumnDragStart($event, index)"
      (dragover)="onColumnDragOver($event, index)"
      (drop)="onColumnDrop($event, index)"
      class="group relative border-b p-1 align-middle font-semibold first:sticky first:left-0 first:top-0 first:z-10 first:rounded-tl-xl last:rounded-tr-xl hover:cursor-grab hover:bg-slate-50"
      [class]="
        columnDragState.draggedOverColumnIndex === index
          ? 'bg-slate-200! first:bg-slate-200!'
          : 'bg-white'
      "
      [style.width.px]="field.width"
    >
      <div class="ml-3 flex items-center gap-3">
        <!-- move handle -->
        <svg-icon
          class="absolute left-0.5 top-1/2 -translate-y-1/2 text-slate-500 opacity-0 group-hover:opacity-100"
          src="/assets/icons/general/drag.svg"
        />

        <!-- field value -->
        <ng-container
          [ngTemplateOutlet]="
            customHeaderCellRef() || defaultHeaderCellTemplate
          "
          [ngTemplateOutletContext]="{ field: field }"
        />

        <!-- sorting -->
        <ng-container
          [ngTemplateOutlet]="headerSortTemplate"
          [ngTemplateOutletContext]="{ field: field }"
        />

        <!-- field resize -->
        <ng-container
          [ngTemplateOutlet]="headerResizeGripTemplate"
          [ngTemplateOutletContext]="{ field: field }"
        />
      </div>
    </th>
  </ng-template>

  <ng-template #headerBulkActionsTemplate>
    @if ((isAnySelected || isAllSelected) && customHeaderBulkActionRef()) {
      <div
        class="absolute bottom-0 left-full top-0 flex items-center gap-3 bg-white p-3"
        [style.width]="bulkActionContainerWidth"
      >
        <ng-container [ngTemplateOutlet]="customHeaderBulkActionRef()" />
      </div>
    }
  </ng-template>

  <ng-template #headerSelectTemplate>
    @if (hasSelectableData) {
      <p-checkbox
        [binary]="true"
        [ngModel]="isAnySelected || isAllSelected"
        [indeterminate]="isAnySelected"
        [disabled]="!hasSelectableRows"
        (onChange)="toggleSelectAllRows()"
      />
    }
  </ng-template>

  <ng-template #headerGroupTemplate>
    @if (hasGroupedData) {
      @if (!isAllGroupsExpanded) {
        <p-button
          size="small"
          severity="contrast"
          variant="text"
          pTooltip="Expand All"
          tooltipPosition="right"
          (click)="expandAllGroups()"
        >
          <ng-template #icon>
            <svg-icon src="/assets/icons/general/chevron-right.svg" />
          </ng-template>
        </p-button>
      }
      @if (isAllGroupsExpanded) {
        <p-button
          size="small"
          severity="contrast"
          variant="text"
          pTooltip="Collapse All"
          tooltipPosition="right"
          (click)="collapseAllGroups()"
        >
          <ng-template #icon>
            <svg-icon src="/assets/icons/general/chevron-down.svg" />
          </ng-template>
        </p-button>
      }
    }
  </ng-template>

  <ng-template #headerSortTemplate let-field="field">
    @if (!field?.sortDirection && field?.sortable) {
      <p-button
        size="small"
        severity="contrast"
        variant="text"
        class="opacity-0 group-hover:opacity-100"
        [pTooltip]="
          field?.displayName
            ? 'Sort: ' + $safeNavigationMigration(field?.displayName)
            : 'Sort: ' + (field.name | humanize | sentenceCase)
        "
        tooltipPosition="right"
        (click)="updateColumnSortDirection(field)"
      >
        <ng-template #icon>
          <svg-icon src="/assets/icons/general/sort-asc.svg" />
        </ng-template>
      </p-button>
    }

    @if (field?.sortDirection && field?.sortable) {
      <p-button
        size="small"
        severity="contrast"
        variant="text"
        [pTooltip]="
          field?.displayName
            ? 'Sort: ' + $safeNavigationMigration(field?.displayName)
            : 'Sort: ' + (field.name | humanize | sentenceCase)
        "
        tooltipPosition="right"
        (click)="updateColumnSortDirection(field)"
      >
        <ng-template #icon>
          @if (field?.sortDirection === "asc") {
            <svg-icon src="/assets/icons/general/sort-asc.svg" />
          }
          @if (field?.sortDirection === "desc") {
            <svg-icon src="/assets/icons/general/sort-desc.svg" />
          }
        </ng-template>
      </p-button>
    }
  </ng-template>

  <ng-template #headerResizeGripTemplate let-field="field">
    <div
      class="absolute inset-y-1.5 right-0 z-1 w-1 translate-x-1/2 cursor-ew-resize transition-all hover:bg-steel-blue-500 group-last-of-type:hidden"
      [class]="
        columnResizeState.isResizing &&
        columnResizeState.columnName === field.name
          ? 'bg-steel-blue-500'
          : 'bg-slate-300'
      "
      [pTooltip]="
        field?.displayName
          ? 'Resize: ' + $safeNavigationMigration(field?.displayName)
          : 'Resize: ' + (field.name | humanize | sentenceCase)
      "
      tooltipPosition="left"
      (mousedown)="onColumnResizeMouseDown($event, field)"
    ></div>
  </ng-template>

  <ng-template #defaultHeaderCellTemplate let-field="field">
    <span class="ml-2">
      @if (field?.displayName) {
        {{ field?.displayName }}
      } @else {
        {{ field.name | humanize | sentenceCase }}
      }
    </span>
  </ng-template>

  <ng-template #defaultHeaderActionTemplate>
    <!-- <span>Actions</span> -->
  </ng-template>

  <ng-template #headerActionStructureTemplate>
    <th
      class="z-30 w-0 rounded-tl-xl border-b border-r p-1 align-middle font-semibold first:sticky first:left-0 first:top-0 first:bg-white!"
    >
      <!-- bulk actions -->
      <ng-container [ngTemplateOutlet]="headerBulkActionsTemplate" />

      <div class="flex grow-0 items-center gap-1">
        <!-- select all action -->
        <ng-container [ngTemplateOutlet]="headerSelectTemplate" />

        <!-- group all action-->
        <ng-container [ngTemplateOutlet]="headerGroupTemplate" />

        <!-- actions -->
        <ng-container
          [ngTemplateOutlet]="
            customHeaderActionRef() || defaultHeaderActionTemplate
          "
        />
      </div>
    </th>
  </ng-template>

  <thead class="select-none border-b-2 border-slate-200 bg-white">
    <tr
      #headerRow
      class="sticky left-0 top-0 z-20 whitespace-nowrap bg-white text-left text-sm tracking-wide text-slate-800"
    >
      @if (
        hasNestedRow || hasCustomAction || hasGroupedData || hasSelectableData
      ) {
        <ng-container [ngTemplateOutlet]="headerActionStructureTemplate" />
      }

      @for (
        field of fields;
        track tableService().trackByField(index, field);
        let index = $index
      ) {
        @if (field.checked) {
          <ng-container
            [ngTemplateOutlet]="headerCellStructureTemplate"
            [ngTemplateOutletContext]="{ field: field, index: index }"
          />
        }
      }
    </tr>
  </thead>
</ng-template>

<ng-template #tableBodyTemplate>
  <ng-template
    #bodyRowStructureTemplate
    let-item="item"
    let-rowIndex="rowIndex"
    let-groupHeader="groupHeader"
    let-data="data"
  >
    <tr
      class="group whitespace-nowrap bg-white text-left text-sm tracking-wide text-slate-600 last:[&_td]:border-b"
    >
      <!-- actions -->
      @if (
        hasNestedRow || hasCustomAction || hasGroupedData || hasSelectableData
      ) {
        <td
          class="w-0 whitespace-normal border-r p-1 align-middle first:sticky first:left-0 first:z-10 group-odd:bg-white group-even:bg-slate-50 group-hover:bg-slate-100"
          [class]="{
            'first:bg-slate-200!':
              rowExpansionState.expandedRowIndex === rowIndex &&
              rowExpansionState.expandedRowGroupHeader === groupHeader,
            'bg-french-blue-50!': selectedRows.has(item.id),
          }"
        >
          <div class="flex items-center gap-1">
            <ng-container
              [ngTemplateOutlet]="bodyRowSelectionTemplate"
              [ngTemplateOutletContext]="{
                item: item,
              }"
            />
            <ng-container
              [ngTemplateOutlet]="bodyRowExpansionTemplate"
              [ngTemplateOutletContext]="{
                rowIndex: rowIndex,
                item: item,
                groupHeader: groupHeader,
              }"
            />
            <ng-container
              [ngTemplateOutlet]="
                customBodyActionRef() || defaultBodyActionTemplate
              "
              [ngTemplateOutletContext]="{ rowIndex: rowIndex, item: item }"
            />
          </div>
        </td>
      }

      <!-- data -->
      @for (field of fields; track tableService().trackByField($index, field)) {
        @if (field.checked) {
          @if (!isRowCellMerged(item, rowIndex, data, field)) {
            <td
              class="whitespace-normal border-r-2 p-1 pl-5! align-middle first:sticky first:left-0 first:z-10 group-odd:bg-white group-even:bg-slate-50 group-hover:bg-slate-100"
              [style.width.px]="field.width"
              [class]="
                columnResizeState.isResizing &&
                columnResizeState.columnName === field.name
                  ? 'border-r-steel-blue-500'
                  : 'border-r-transparent'
              "
              [class.bg-slate-200!]="
                rowExpansionState.expandedRowIndex === rowIndex &&
                groupHeader === rowExpansionState.expandedRowGroupHeader
              "
              [class.bg-french-blue-50!]="selectedRows.has(item.id)"
              [attr.rowspan]="getMergedRowspan(rowIndex, data, field)"
            >
              <ng-container
                [ngTemplateOutlet]="
                  customBodyCellRef() || defaultBodyCellTemplate
                "
                [ngTemplateOutletContext]="{
                  item: item,
                  field: field,
                  rowIndex: rowIndex,
                }"
              />
            </td>
          }
        }
      }
    </tr>
  </ng-template>

  <ng-template #bodyRowSelectionTemplate let-item="item">
    @if (hasSelectableData) {
      <p-checkbox
        [binary]="true"
        [ngModel]="selectedRows.has(item.id)"
        [disabled]="excludedRowsFromSelection().includes(item.id)"
        (onChange)="toggleRowSelection(item)"
      />
    }
  </ng-template>

  <ng-template #bodyGroupSelectionTemplate let-group="group">
    @if (hasSelectableData) {
      <p-checkbox
        [binary]="true"
        [ngModel]="selectedGroups.has(group.groupHeader)"
        [disabled]="!hasSelectableGroupRows(group)"
        (onChange)="toggleGroupSelection(group)"
      />
    }
  </ng-template>

  <ng-template #bodyGroupActionTemplate let-group="group">
    @if (!group.expanded) {
      <p-button
        size="small"
        severity="contrast"
        variant="text"
        pTooltip="Expand"
        tooltipPosition="right"
        (click)="expandGroup(group)"
      >
        <ng-template #icon>
          <svg-icon src="/assets/icons/general/chevron-right.svg" />
        </ng-template>
      </p-button>
    }
    @if (group.expanded) {
      <p-button
        size="small"
        severity="contrast"
        variant="text"
        pTooltip="Collapse"
        tooltipPosition="right"
        (click)="collapseGroup(group)"
      >
        <ng-template #icon>
          <svg-icon src="/assets/icons/general/chevron-down.svg" />
        </ng-template>
      </p-button>
    }
  </ng-template>

  <ng-template
    #bodyRowExpansionTemplate
    let-rowIndex="rowIndex"
    let-item="item"
    let-groupHeader="groupHeader"
  >
    @if (hasNestedRow) {
      @if (
        rowExpansionState.expandedRowIndex !== rowIndex ||
        (groupHeader &&
          groupHeader !== rowExpansionState.expandedRowGroupHeader)
      ) {
        <p-button
          size="small"
          severity="contrast"
          variant="text"
          pTooltip="Expand"
          tooltipPosition="right"
          (click)="toggleNestedRow(item, rowIndex, groupHeader)"
        >
          <ng-template #icon>
            <svg-icon src="/assets/icons/general/chevron-right.svg" />
          </ng-template>
        </p-button>
      }
      @if (
        (rowExpansionState.expandedRowIndex === rowIndex && !groupHeader) ||
        (groupHeader &&
          groupHeader === rowExpansionState.expandedRowGroupHeader &&
          rowExpansionState.expandedRowIndex === rowIndex)
      ) {
        <p-button
          size="small"
          severity="contrast"
          variant="text"
          pTooltip="Collapse"
          tooltipPosition="right"
          (click)="toggleNestedRow(item, rowIndex, groupHeader)"
        >
          <ng-template #icon>
            <svg-icon src="/assets/icons/general/chevron-down.svg" />
          </ng-template>
        </p-button>
      }
    }
  </ng-template>

  <ng-template #defaultBodyCellTemplate let-item="item" let-field="field">
    <span>{{ item[field.name] }}</span>
  </ng-template>

  <ng-template
    #defaultBodyActionTemplate
    let-item="item"
    let-rowIndex="rowIndex"
  >
    <!-- implement default template if needed -->
  </ng-template>

  <!-- groupedData -->
  @if (hasGroupedData) {
    @for (group of data.groupedData; track trackByGroup($index, group)) {
      <tbody>
        <!-- group header -->
        <tr
          class="sticky left-0 top-[2.55rem] z-20 whitespace-nowrap text-left"
        >
          <th
            class="border-b bg-white text-sm font-normal tracking-wider"
            [attr.colspan]="checkedFields.length + 1"
          >
            <div
              class="sticky left-0 top-0 flex h-[2.55rem] w-fit items-center gap-3 bg-white pl-1"
            >
              <!-- group selection action -->
              <ng-container
                [ngTemplateOutlet]="bodyGroupSelectionTemplate"
                [ngTemplateOutletContext]="{ group: group }"
              />
              <!-- group expansion action -->
              <ng-container
                [ngTemplateOutlet]="bodyGroupActionTemplate"
                [ngTemplateOutletContext]="{
                  group: group,
                }"
              />
              <span class="flex w-fit items-center gap-1">
                @for (
                  groupedField of groupedFields;
                  track tableService().trackByField($index, groupedField);
                  let last = $last
                ) {
                  <!-- group by field value -->
                  <ng-container
                    [ngTemplateOutlet]="
                      customBodyCellRef() || defaultBodyCellTemplate
                    "
                    [ngTemplateOutletContext]="{
                      item: group.groupData[0],
                      field: groupedField,
                    }"
                  />
                  <!-- separator -->
                  @if (!last) {
                    <span>
                      <svg-icon
                        class="text-slate-400"
                        src="/assets/icons/general/chevron-right.svg"
                      />
                    </span>
                  }
                }
              </span>
              <!-- group count -->
              <div
                class="flex items-center gap-3 text-sm font-normal text-slate-500"
              >
                <span>|</span>
                <span>
                  {{ group.count }}
                  <ng-container [ngPlural]="group.count">
                    <ng-template ngPluralCase="=1">item</ng-template>
                    <ng-template ngPluralCase="other">items</ng-template>
                  </ng-container>
                </span>
              </div>
            </div>
          </th>
        </tr>
        <!-- group rows -->
        @if (group.expanded) {
          @for (
            item of group.groupData;
            track tableService().trackById(rowIndex, item);
            let rowIndex = $index
          ) {
            <ng-container
              [ngTemplateOutlet]="bodyRowStructureTemplate"
              [ngTemplateOutletContext]="{
                item: item,
                rowIndex: rowIndex,
                groupHeader: group.groupHeader,
                data: group.groupData,
              }"
            />
            <!-- row to hold nested template -->
            @if (
              rowIndex === rowExpansionState.expandedRowIndex &&
              hasNestedRow &&
              group.groupHeader === rowExpansionState.expandedRowGroupHeader
            ) {
              <ng-container
                [ngTemplateOutlet]="bodyNestedRowTemplate"
                [ngTemplateOutletContext]="{ item: item, rowIndex: rowIndex }"
              />
            }
          }
        }
      </tbody>
    }
  }

  <!-- flatData -->
  @if (!hasGroupedData) {
    <tbody>
      <!-- rows -->
      @for (
        item of data.ungroupedData;
        track tableService().trackById(rowIndex, item);
        let rowIndex = $index
      ) {
        <ng-container
          [ngTemplateOutlet]="bodyRowStructureTemplate"
          [ngTemplateOutletContext]="{
            item: item,
            rowIndex: rowIndex,
            groupHeader: null,
            data: data.ungroupedData,
          }"
        />
        <!-- row to hold nested template -->
        @if (rowIndex === rowExpansionState.expandedRowIndex && hasNestedRow) {
          <ng-container
            [ngTemplateOutlet]="bodyNestedRowTemplate"
            [ngTemplateOutletContext]="{ item: item, rowIndex: rowIndex }"
          />
        }
      }
    </tbody>
  }
</ng-template>

<ng-template #bodyNestedRowTemplate let-item="item" let-rowIndex="rowIndex">
  <tr>
    <td [attr.colspan]="checkedFields.length + 1">
      <!-- nested row -->
      <div class="bg-slate-200 p-2">
        <ng-container
          [ngTemplateOutlet]="nestedRowRef()"
          [ngTemplateOutletContext]="{
            item: item,
            rowIndex: rowIndex,
          }"
        />
      </div>
    </td>
  </tr>
</ng-template>
